import { Button, Card, Flex, Form, Modal, ModalProps, notification, Segmented, Select, Spin, Table, Upload } from 'antd'
import React, { useState } from 'react'
import { FormItem } from '../../components/form-item'
import { useTranslation } from 'react-i18next'
import { useBulkStoreCandidate } from '../hooks'
import { Campaign } from '../../campaign/campaign'
import { DeleteOutlined, FileExcelOutlined } from '@ant-design/icons'
import * as XLSX from 'xlsx'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import cn from 'clsx'

export type CampaignImportModalProps = {
    campaignId: Campaign['id']
} & ModalProps

type ParsedData = {
    headers: string[]
    rows: string[][]
}

const separators = [
    { value: ',', label: ',' },
    { value: ';', label: ';' },
    { value: '\t', label: 'Tab' },
    { value: ':', label: ':' },
    { value: '`', label: '`' },
    { value: '^', label: '^' },
    { value: '~', label: '~' },
    { value: '#', label: '#' },
]

function guessSeparator(text: string) {
    const scores: Record<string, number> = {}

    separators.forEach(({ value }) => {
        scores[value] = text.split(value).length
    })

    const best = Object.entries(scores).reduce((a, b) => (b[1] > a[1] ? b : a))
    return best[0]
}

async function parseCsv(uploadedFile: File, separator?: string) {
    const text = await uploadedFile.text()
    const lines = text.split('\n').filter((line) => line.trim())

    if (!separator) {
        separator = guessSeparator(lines[0])
    }

    const headers = lines[0].split(separator).map((h) => h.trim().replace(/"/g, ''))
    const rows = lines.slice(1).map((line_1) => line_1.split(separator).map((cell) => cell.trim().replace(/"/g, '')))

    return { headers, rows, separator }
}

function parseExcel(uploadedFile: File) {
    return uploadedFile
        .arrayBuffer()
        .then((arrayBuffer) => {
            const workbook = XLSX.read(arrayBuffer, { type: 'array' })
            const sheetName = workbook.SheetNames[0]
            const worksheet = workbook.Sheets[sheetName]
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
            }) as string[][]

            const headers = jsonData[0] || []
            const rows = jsonData.slice(1)

            return { headers, rows }
        })
        .catch((error) => {
            throw error
        })
}

export function CandidateImportModal({ campaignId, onCancel, ...rest }: CampaignImportModalProps) {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [file, setFile] = useState<File | null>(null)
    const [parsedData, setParsedData] = useState<ParsedData | null>(null)
    const [errors, setErrors] = useState<Record<number, string>>({})
    const [type, setType] = useState<'csv' | 'excel'>('excel')
    const [separator, setSeparator] = useState<string | undefined>(undefined)
    const mutation = useBulkStoreCandidate(campaignId)
    const [form] = Form.useForm()

    const resetState = () => {
        setFile(null)
        setParsedData(null)
        setErrors({})
    }

    const firstNameIndex = Form.useWatch('firstName', form) || 0
    const lastNameIndex = Form.useWatch('lastName', form) || 1
    const emailIndex = Form.useWatch('email', form) || 2
    const phoneIndex = Form.useWatch('phone', form) || 3

    const queryClient = useQueryClient()
    const selectOptions = parsedData?.headers
        .map((header, index) => {
            return { value: index, label: header }
        })
        .filter((option) => option)

    return (
        <Modal
            {...rest}
            title={t('Import candidates')}
            onOk={() =>
                mutation
                    .mutateAsync({
                        candidates:
                            parsedData?.rows.map((row) => {
                                const phone = row[phoneIndex]

                                return {
                                    firstName: row[firstNameIndex],
                                    lastName: row[lastNameIndex],
                                    phone: phone.startsWith('+') ? phone : `+${phone}`,
                                    email: row[emailIndex],
                                }
                            }) || [],
                    })
                    .then(() => {
                        resetState()

                        const total = parsedData?.rows.length || 0
                        let counter = 0

                        const interval = setInterval(() => {
                            queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] })
                            counter++

                            if (counter >= total) {
                                clearInterval(interval)
                            }
                        }, 1000)

                        onCancel?.(undefined as never)
                    })
                    .catch((error) => {
                        if (isAxiosError(error)) {
                            const data = error.response?.data

                            if ('errors' in data) {
                                const errors = Object.keys(data.errors).reduce<Record<number, string>>((carry, key) => {
                                    const [, id, field] = key.split('.')

                                    carry[Number(id)] = field

                                    return carry
                                }, {})

                                setErrors(errors)
                            }

                            // Wait for render
                            setTimeout(() => {
                                document.querySelector('.td-validation-error')?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center',
                                })
                            }, 0)
                        }
                    })
            }
            okText={t('Import')}
            onCancel={onCancel}
            okButtonProps={{
                loading: mutation.isPending,
                disabled: (parsedData?.rows.length || 0) === 0,
            }}
            width={1000}
        >
            <Spin spinning={loading}>
                <Flex
                    gap="middle"
                    align="start"
                    vertical
                >
                    <Upload.Dragger
                        className="w-full"
                        beforeUpload={async (file) => {
                            setLoading(true)
                            setFile(file)

                            try {
                                const fileExtension = file.name.split('.').pop()?.toLowerCase()

                                if (fileExtension === 'csv') {
                                    setType('csv')
                                    const { headers, rows, separator } = await parseCsv(file)
                                    setParsedData({ headers, rows })
                                    setSeparator(separator)
                                } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
                                    setType('excel')
                                    const { headers, rows } = await parseExcel(file)
                                    setParsedData({ headers, rows })
                                } else {
                                    notification.error({
                                        message: t('Unsupported filetype supplied'),
                                    })
                                }
                            } catch {
                                notification.error({
                                    message: t('Failed to read file contents'),
                                })
                            }

                            setLoading(false)

                            return false
                        }}
                        fileList={[]}
                        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    >
                        <p className="ant-upload-drag-icon">
                            <FileExcelOutlined />
                        </p>
                        <p className="ant-upload-text">{t('Click or drag an Excel/CSV file to upload')}</p>
                        <p className="ant-upload-hint">{t('Only Excel (xls, xlsx) or CSV (csv) files are allowed')}</p>
                    </Upload.Dragger>

                    {file && (
                        <>
                            {type === 'csv' && (
                                <Card
                                    size="small"
                                    className="w-full"
                                    title={t('CSV Separator')}
                                >
                                    <Segmented
                                        value={separator}
                                        onChange={(separator) => {
                                            setSeparator(separator)
                                            parseCsv(file!, separator).then((data) => {
                                                setParsedData(data)
                                            })
                                        }}
                                        options={separators}
                                    />
                                </Card>
                            )}
                            <Card
                                size="small"
                                className="w-full"
                                title={t('Map columns')}
                            >
                                <Form
                                    layout="vertical"
                                    className="w-full"
                                    form={form}
                                >
                                    <Flex gap="large">
                                        <FormItem
                                            className="w-full"
                                            name="firstName"
                                            label={t('First name')}
                                        >
                                            <Select options={selectOptions} />
                                        </FormItem>
                                        <FormItem
                                            className="w-full"
                                            name="lastName"
                                            label={t('Last name')}
                                        >
                                            <Select options={selectOptions} />
                                        </FormItem>
                                        <FormItem
                                            className="w-full"
                                            name="phone"
                                            label={t('Phone')}
                                        >
                                            <Select options={selectOptions} />
                                        </FormItem>
                                        <FormItem
                                            className="w-full"
                                            name="email"
                                            label={t('Email')}
                                        >
                                            <Select options={selectOptions} />
                                        </FormItem>
                                    </Flex>
                                </Form>
                            </Card>

                            <Table
                                className="w-full"
                                dataSource={parsedData?.rows}
                                pagination={false}
                                scroll={{
                                    y: 400,
                                }}
                                columns={[
                                    {
                                        title: 'First name',
                                        key: '',
                                        width: 150,
                                        render: (candidate, _, index) => (
                                            <span
                                                className={cn({
                                                    'td-validation-error':
                                                        index in errors && errors[index] === 'firstName',
                                                })}
                                            >
                                                {candidate[firstNameIndex]}
                                            </span>
                                        ),
                                    },
                                    {
                                        title: 'Last name',
                                        key: 'lastName',
                                        width: 170,
                                        render: (candidate, _, index) => (
                                            <span
                                                className={cn({
                                                    'td-validation-error':
                                                        index in errors && errors[index] === 'lastName',
                                                })}
                                            >
                                                {candidate[lastNameIndex]}
                                            </span>
                                        ),
                                    },
                                    {
                                        title: 'Phone',
                                        key: 'phone',
                                        width: 150,
                                        render: (candidate, _, index) => (
                                            <span
                                                className={cn({
                                                    'td-validation-error': index in errors && errors[index] === 'phone',
                                                })}
                                            >
                                                {candidate[phoneIndex]}
                                            </span>
                                        ),
                                    },
                                    {
                                        title: 'Email',
                                        key: 'email',
                                        render: (candidate, _, index) => (
                                            <span
                                                className={cn({
                                                    'td-validation-error': index in errors && errors[index] === 'email',
                                                })}
                                            >
                                                {candidate[emailIndex]}
                                            </span>
                                        ),
                                    },
                                    {
                                        title: '',
                                        key: '',
                                        width: 80,
                                        render: (candidate, _, index) => (
                                            <Button
                                                type="text"
                                                onClick={() => {
                                                    setErrors({}) // clear errors, they won't line up anymore

                                                    setParsedData((data) => {
                                                        if (data === null) {
                                                            return null
                                                        }

                                                        data?.rows.splice(index, 1)

                                                        return { ...data }
                                                    })
                                                }}
                                                icon={<DeleteOutlined />}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </>
                    )}
                </Flex>
            </Spin>
        </Modal>
    )
}
